import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import message from 'antd/es/message';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpPage } from 'new-components/OpPage/OpPage';
import { queryRequest } from 'utils/queryRequest';
import { OpInfo } from 'new-components/DLS/OpInfo/OpInfo';

import { useValidateAndSetToken } from './helpers/useValidateAndSetToken';

import './CloudKeyUnlockPage.scss';

const CloudKeyUnlockPage = () => {
  const { t, i18n } = useTranslation();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [status, setStatus] = useState('');
  const [unlockEntryId, setUnlockEntryId] = useState<number>();

  const {
    isLoading,
    errorMessage,
    errorDescription,
    token,
    decodedJwt: {
      iat: tokenIssuedAt,
      exp: tokenExpirationDate,
      entryId: tokenEntryId,
      entryData: tokenEntryData,
    },
  } = useValidateAndSetToken({
    messageApi,
  });

  const formatDate = (ts: number) => {
    const date = new Date(ts * 1000);
    const locale = i18n.language;
    const datePart = date.toLocaleDateString(locale);
    const timePart = date
      .toLocaleTimeString(locale, {
        timeStyle: 'short',
      })
      .toLowerCase();
    return `${datePart} ${timePart}`;
  };

  const cloudKeyUnlock = async (entryIdToUnlock: number) => {
    setStatus('processing');
    setUnlockEntryId(entryIdToUnlock);

    const currentIdentityLanguage = localStorage.getItem('i18nextLng') || 'en';

    const useCloudKeyUnlockTokenResponse = await queryRequest({
      apiEndpointName: 'useCloudKeyUnlockToken',
      parameters: [token, { entryId: entryIdToUnlock }],
      currentIdentityLanguage,
    });

    if (useCloudKeyUnlockTokenResponse.statusCode >= 400) {
      messageApi.error(useCloudKeyUnlockTokenResponse.json.message);
      setStatus('fail');
    } else {
      setStatus('success');
      messageApi.success(t('Unlock request successfully sent'));
    }
  };

  const renderStatus = () => {
    switch (status) {
      case 'processing':
        return (
          <LoadingOutlined className="cloud-key-unlock-page__loading-icon" />
        );
      case 'success':
        return (
          <CheckCircleFilled className="cloud-key-unlock-page__success-icon" />
        );
      case 'fail':
        return (
          <CloseCircleFilled className="cloud-key-unlock-page__error-icon" />
        );

      default:
        return null;
    }
  };

  const createContent = () => {
    if (isLoading) {
      return (
        <div className="cloud-key-unlock-page__loader">
          <LoadingOutlined />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <OpInfo
          closable // Need to be able to get the space to see the buttons in the case of font scaling
          type="error"
          title={errorMessage}
          description={errorDescription}
        />
      );
    }

    return (
      <div className="cloud-key-unlock-page__content">
        <h3 className="cloud-key-unlock-page__entry-list-label">
          {t('Tap to Unlock an Entry')}
        </h3>

        {typeof tokenEntryId === 'number' ? (
          <div className="cloud-key-unlock-page__entry">
            <div style={{ minWidth: 'fit-content' }}>
              <OpButton
                type="primary"
                onClick={() => {
                  cloudKeyUnlock(tokenEntryId!);
                }}
              >
                {t('Click to unlock')}
              </OpButton>
            </div>
            {renderStatus()}
          </div>
        ) : (
          <div className="cloud-key-unlock-page__entry-list">
            {(tokenEntryData || []).map(({ entryId, uiLabel }) => (
              <div key={entryId} className="cloud-key-unlock-page__entry">
                <div style={{ minWidth: 'fit-content' }}>
                  <OpButton
                    key={entryId}
                    type="primary"
                    onClick={() => cloudKeyUnlock(entryId)}
                  >
                    {uiLabel}
                  </OpButton>
                </div>
                {unlockEntryId === entryId && renderStatus()}
              </div>
            ))}
          </div>
        )}

        {tokenIssuedAt && tokenExpirationDate && (
          <div className="cloud-key-unlock-page__footer">
            {t('Valid {{fromDate}} - {{toDate}}', {
              fromDate: formatDate(tokenIssuedAt),
              toDate: formatDate(tokenExpirationDate),
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <OpPage className="cloud-key-unlock-page">
      {/** For messages to be displayed */}
      {messageContextHolder}

      {createContent()}
      <img
        className="cloud-key-unlock-page__logo"
        alt="Avigilon Alta"
        src={require('assets/img/avigilon-alta-logo--white.svg')}
      />
    </OpPage>
  );
};

export default CloudKeyUnlockPage;
